import React, { useEffect, useRef } from "react";

interface ModalShellProps {
  /** Closes the dialog — wired to Escape, the backdrop, and nothing else. */
  onClose: () => void;
  /** Accessible name; pass the id of the dialog's heading element. */
  labelledBy?: string;
  /** Accessible description; pass the id of the dialog's body text. */
  describedBy?: string;
  /** Max-width utility for the panel (default "max-w-md"). */
  maxWidthClassName?: string;
  children: React.ReactNode;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The one dialog foundation every modal builds on: fixed overlay with a
 * blurred backdrop (click to close), Escape to close, body scroll lock,
 * dialog semantics, and a keyboard focus trap — focus moves into the panel on
 * open, Tab/Shift+Tab cycle inside it, and focus returns to the opener on
 * close. Variants (confirmation, form, note) provide the panel content.
 */
const ModalShell: React.FC<ModalShellProps> = ({
  onClose,
  labelledBy,
  describedBy,
  maxWidthClassName = "max-w-md",
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  // Read fresh on every keydown so a re-created onClose isn't captured stale.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;

    // Move focus into the dialog: the first focusable control, or the panel.
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !panel) return;

      // Trap: cycle Tab / Shift+Tab within the panel's focusable controls.
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }
      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === firstEl || active === panel)) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && active === lastEl) {
        event.preventDefault();
        firstEl.focus();
      } else if (active && !panel.contains(active)) {
        // Focus escaped (e.g. programmatically) — pull it back in.
        event.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.body.style.overflow = previousOverflow;
      opener?.focus?.();
    };
    // Mount-only: re-running on every render would re-steal focus. onClose is
    // read through onCloseRef, so it is never stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-[110] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={panelRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          aria-describedby={describedBy}
          className={`relative w-full ${maxWidthClassName} max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-xl dark:bg-primary-10 border border-grey-70 dark:border-grey-30`}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default ModalShell;
